import { UFOv2 } from '@atlassian/post-office-frontend-performance-tracking';
import { MessageContextProvider, type MessageContextData } from '@post-office/message-context';
import { RecommendationProvider } from '@post-office/recommendation-context';
import type { MessageCreationType } from '@post-office/shared-contracts/message-template';
import {
	type FunctionComponent,
	type ReactNode,
	Suspense,
	type SuspenseProps,
	useMemo,
} from 'react';
import { MaybeI18nProvider } from '../i18n/i18nProvider';
import { ChoreographerWrapper } from './choreographer-wrapper';
import { FeatureFlaggedUfoWrapper } from './ufo-wrapper';
import { TriggeredMessageValidator } from './triggered-message-validator';
import { MessageAnalyticsContextProvider } from '../message-analytics-context';
import { MaybeManualMessageViewedEvent } from '@post-office/placement-contexts/maybe-manual-message-viewed-event';
import { type MessageCategory } from '../types';
import type { WaitForTriggerFunction } from '../hooks/use-wait-for-trigger';
import { type JSX } from 'react/jsx-runtime';
import { useSubscriptionConfig } from '../create-placement-component/providers/stable-graphql-subscription';

type ComponentProps = {
	transactionAccountId?: string;
};

type ProviderProps = {
	messageTemplateId: string;
	messageInstanceId: string;
	messageCategory: MessageCategory;
	transactionAccountId?: string;
	recommendationSession?: {
		sessionId: string;
		entityid: string;
	};
	product?: string;
	loadingFallback?: React.ReactNode;
	postOffice?: {
		analyticsDetails?: MessageContextData['analyticsDetails'];
	};
	triggerId?: string;
	messageCreationType?: MessageCreationType;
	messageSuccessComponent?: JSX.Element;
	componentProps: ComponentProps;
	isUFOEnabled: boolean;
	children?: ReactNode;
	SuspenseBoundary?: FunctionComponent<SuspenseProps>;
	waitForTrigger?: WaitForTriggerFunction;
	configSetId?: string;
	journeyId?: string;
	journeyTriggerId?: string;
	placementId?: string;
	isMaybeI18nProviderEnabledCallback?: () => boolean;
};

const DEFAULT_RECOMMENDATION_SESSION = {};

export const MessageRendererProvider = ({
	messageInstanceId,
	messageTemplateId,
	messageCategory,
	recommendationSession,
	messageSuccessComponent,
	componentProps,
	loadingFallback,
	triggerId,
	messageCreationType,
	postOffice,
	isUFOEnabled,
	children,
	SuspenseBoundary = Suspense,
	waitForTrigger,
	configSetId,
	journeyId,
	journeyTriggerId,
	placementId,
	isMaybeI18nProviderEnabledCallback,
}: ProviderProps): JSX.Element => {
	const subscriptionConfig = useSubscriptionConfig();
	const subscriptionEnabled = subscriptionConfig.subscription?.enabled ?? false;

	/**
	 * Type guard: messageContextMemo must match MessageContextData exactly.
	 * If a field is missing or wrong type, the type annotation will catch it.
	 */
	const loadingComponent = useMemo(
		() =>
			isUFOEnabled ? (
				<UFOv2.UFOLoadHold name={UFOv2.HoldNames.MESSAGE}>
					{loadingFallback ?? null}
				</UFOv2.UFOLoadHold>
			) : (
				<>{loadingFallback ?? null}</>
			),
		[loadingFallback, isUFOEnabled],
	);

	const messageContextMemo = useMemo<MessageContextData>(
		() => ({
			messageInstanceId,
			messageTemplateId,
			triggerId,
			messageCreationType,
			transactionAccountId: componentProps.transactionAccountId,
			analyticsDetails: {
				...postOffice?.analyticsDetails,
				subscriptionEnabled,
			},
			journeyId,
			journeyTriggerId,
			configSetId,
		}),
		[
			messageInstanceId,
			messageTemplateId,
			triggerId,
			messageCreationType,
			componentProps.transactionAccountId,
			postOffice?.analyticsDetails,
			subscriptionEnabled,
			journeyId,
			journeyTriggerId,
			configSetId,
		],
	);

	const isMaybeI18nEnabled = isMaybeI18nProviderEnabledCallback?.() ?? false;

	const messageContent = (
		<FeatureFlaggedUfoWrapper messageTemplateId={messageTemplateId} isEnabled={isUFOEnabled}>
			<MessageContextProvider value={messageContextMemo}>
				<RecommendationProvider value={recommendationSession ?? DEFAULT_RECOMMENDATION_SESSION}>
					<SuspenseBoundary fallback={loadingComponent}>
						<MessageAnalyticsContextProvider>
							<MaybeManualMessageViewedEvent isManual={!!waitForTrigger}>
								<MaybeI18nProvider
									messageTemplateId={messageTemplateId}
									placementId={placementId}
									isI18nMigrationInAppEnabled={isMaybeI18nEnabled}
								>
									{!waitForTrigger && messageSuccessComponent}
									{children}
								</MaybeI18nProvider>
							</MaybeManualMessageViewedEvent>
						</MessageAnalyticsContextProvider>
					</SuspenseBoundary>
				</RecommendationProvider>
			</MessageContextProvider>
		</FeatureFlaggedUfoWrapper>
	);

	// Triggered messages use TriggeredMessageValidator (validates waitForTrigger was called)
	// Check if triggered by checking if waitForTrigger exists
	if (waitForTrigger) {
		return (
			<TriggeredMessageValidator waitForTrigger={waitForTrigger}>
				{messageContent}
			</TriggeredMessageValidator>
		);
	}

	// Regular messages use automatic choreography
	return (
		<ChoreographerWrapper
			messageId={messageInstanceId}
			messageTemplateId={messageTemplateId}
			messageCategory={messageCategory}
		>
			{messageContent}
		</ChoreographerWrapper>
	);
};
